import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { EntityFilterConfig, EntityFilterOption } from '../types'
import * as s from '../Table.css'
import * as es from './EntityFilterChip.css'
import { TablePopover } from './TablePopover'
import { useT } from '../../../../app/lib/i18n'

function PlusIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

function XIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export function EntityFilterChip({
  label,
  value,
  onChange,
  config,
  presentation = 'popover',
}: {
  label: string
  value: string[] | null
  onChange: (v: string[] | null) => void
  config: EntityFilterConfig
  presentation?: 'popover' | 'inline'
}) {
  const t = useT()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [options, setOptions] = useState<EntityFilterOption[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [labelCache, setLabelCache] = useState<Record<string, string>>({})

  const selectedIds = value ?? []
  const active = selectedIds.length > 0
  const pageSize = config.pageSize ?? 20
  const fetchPageRef = useRef(config.fetchPage)
  const resolveLabelsRef = useRef(config.resolveLabels)
  fetchPageRef.current = config.fetchPage
  resolveLabelsRef.current = config.resolveLabels

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setPage(1)
    void fetchPageRef
      .current({ search: debouncedSearch, page: 1, limit: pageSize })
      .then((res) => {
        if (cancelled) return
        setOptions(res.data)
        setTotal(res.total)
        setLabelCache((prev) => {
          const next = { ...prev }
          for (const opt of res.data) next[opt.id] = opt.label
          return next
        })
      })
      .catch(() => {
        if (cancelled) return
        setOptions([])
        setTotal(0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, debouncedSearch, pageSize])

  useEffect(() => {
    if (!active || !resolveLabelsRef.current) return
    const missing = selectedIds.filter((id) => !labelCache[id])
    if (missing.length === 0) return
    let cancelled = false
    void resolveLabelsRef.current(missing).then((resolved) => {
      if (cancelled) return
      setLabelCache((prev) => ({ ...prev, ...resolved }))
    })
    return () => {
      cancelled = true
    }
  }, [active, selectedIds, labelCache])

  const loadMore = useCallback(async () => {
    const nextPage = page + 1
    setLoadingMore(true)
    try {
      const res = await fetchPageRef.current({
        search: debouncedSearch,
        page: nextPage,
        limit: pageSize,
      })
      setPage(nextPage)
      setOptions((prev) => {
        const seen = new Set(prev.map((o) => o.id))
        const merged = [...prev]
        for (const opt of res.data) {
          if (!seen.has(opt.id)) merged.push(opt)
        }
        return merged
      })
      setTotal(res.total)
      setLabelCache((prev) => {
        const next = { ...prev }
        for (const opt of res.data) next[opt.id] = opt.label
        return next
      })
    } finally {
      setLoadingMore(false)
    }
  }, [page, debouncedSearch, pageSize])

  const toggle = (id: string, optLabel?: string) => {
    const set = new Set(selectedIds)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    if (optLabel) {
      setLabelCache((prev) => ({ ...prev, [id]: optLabel }))
    }
    onChange(set.size ? [...set] : null)
  }

  const removeId = (id: string) => {
    const next = selectedIds.filter((x) => x !== id)
    onChange(next.length ? next : null)
  }

  const chipValueText = useMemo(() => {
    if (!active) return ''
    if (selectedIds.length === 1) {
      return labelCache[selectedIds[0]!] ?? selectedIds[0]!
    }
    return String(selectedIds.length)
  }, [active, selectedIds, labelCache])

  const hasMore = options.length < total

  const panel = (
        <div className={es.panel}>
          <div className={es.searchWrap}>
            <input
              className={es.searchInput}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                config.searchPlaceholder ??
                t('shared.listView.entityFilter.searchPlaceholder')
              }
              autoFocus
            />
          </div>

          {active && (
            <div className={es.selectedRow}>
              {selectedIds.map((id) => (
                <span key={id} className={es.selectedPill}>
                  <span className={es.selectedPillLabel}>
                    {labelCache[id] ?? id}
                  </span>
                  <button
                    type="button"
                    className={es.selectedPillRemove}
                    aria-label={t('shared.listView.entityFilter.remove')}
                    onClick={() => removeId(id)}
                  >
                    <XIcon size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className={es.results}>
            {loading && (
              <div className={es.status}>{t('shared.common.loading')}</div>
            )}
            {!loading && options.length === 0 && (
              <div className={es.status}>
                {config.emptyMessage ??
                  t('shared.listView.entityFilter.empty')}
              </div>
            )}
            {!loading &&
              options.map((opt) => {
                const checked = selectedIds.includes(opt.id)
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className={`${es.resultRow}${checked ? ' is-selected' : ''}`}
                    onClick={() => toggle(opt.id, opt.label)}
                  >
                    <input
                      type="checkbox"
                      className={es.resultCheck}
                      checked={checked}
                      onChange={() => toggle(opt.id, opt.label)}
                      onClick={(e) => e.stopPropagation()}
                      tabIndex={-1}
                    />
                    <span className={es.resultText}>
                      <span className={es.resultLabel}>{opt.label}</span>
                      {opt.subtitle ? (
                        <span className={es.resultSubtitle}>{opt.subtitle}</span>
                      ) : null}
                    </span>
                  </button>
                )
              })}
            {!loading && hasMore && (
              <button
                type="button"
                className={es.loadMore}
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {loadingMore
                  ? t('shared.common.loading')
                  : t('shared.listView.entityFilter.loadMore')}
              </button>
            )}
          </div>

          {active && (
            <>
              <div className={s.popoverDivider} />
              <div className={s.popoverFoot}>
                <button
                  type="button"
                  className={s.popoverFootBtn}
                  onClick={() => onChange(null)}
                >
                  {t('shared.listView.entityFilter.clear')}
                </button>
                <button
                  type="button"
                  className={s.popoverFootBtn}
                  onClick={() => setOpen(false)}
                >
                  {t('shared.listView.entityFilter.done')}
                </button>
              </div>
            </>
          )}
        </div>
  )

  if (presentation === 'inline') {
    return (
      <div className={s.filterSheetControl}>
        <button
          ref={anchorRef}
          type="button"
          className={`${s.sortConfigBtn} ${s.filterSheetTrigger}${open || active ? ' is-active' : ''}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {!active && <PlusIcon size={12} />}
          <span className={s.filterChipLabel}>{label}</span>
          {active ? (
            <span className={s.filterSheetTriggerValue}>
              {selectedIds.length === 1 ? chipValueText : `${chipValueText} selected`}
            </span>
          ) : null}
        </button>
        {open ? <div className={s.filterSheetInlinePanel}>{panel}</div> : null}
      </div>
    )
  }

  return (
    <div className={s.popoverAnchor}>
      <button
        ref={anchorRef}
        type="button"
        className={`${s.filterChip}${active ? ' is-active' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {!active && <PlusIcon size={12} />}
        <span className={s.filterChipLabel}>{label}</span>
        {active && (
          <span className={s.filterChipValues}>
            {selectedIds.length === 1 ? (
              <span>{chipValueText}</span>
            ) : (
              <span className={s.filterChipPill}>{chipValueText}</span>
            )}
          </span>
        )}
      </button>
      <TablePopover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        {panel}
      </TablePopover>
    </div>
  )
}
